// Rail step 3 "První kroky k aplikaci" - ONE act: the client's own Apple
// Developer account (F135a).
//
// Ground truth for every sentence on this page is the guidance the platform
// itself gives to people who bought a standalone mobile app: buddy repo
// `turborepo/apps/dashboard/messages/{cs,en}.json`, namespace
// `features.mobileApp.appleDeveloper` (read from origin/main 2026-08-11) -
// own account required (99 USD a year), register as an ORGANIZATION on
// developer.apple.com, wait for Apple to verify the company (1 to 2 working
// days, sometimes a phone call), then confirm and hand over the Team ID plus
// the App Store Connect e-mail. F105 law: the client meets the same words
// here and in the product.
//
// Nothing about the questionnaire and nothing about the release lives here,
// and NOTHING follows the confirmation (Pavel: "pod tím jsou ještě nějaký
// další věci (...) to si myslím, že by tam vůbec nemělo být").
import { useState, type FormEvent } from "react";
import { ActionError, DataError, refreshClientFact } from "../shared";
import { NonBreakingDunsText, appleChecklistLock, appleFacts, type AppTrackProps } from "./app-track-shared";

/**
 * Apple's own D-U-N-S tools. Both verified live 2026-08-13 against a real 404
 * control on the same host, so the 200 means a page and not a catch-all.
 */
const DUNS_LOOKUP_URL = "https://developer.apple.com/enroll/duns-lookup/";

export function AppleStep(props: AppTrackProps) {
  const [confirmationState, setConfirmationState] = useState<"idle" | "sending" | "done" | "error">("idle");
  const { section, account, confirmed, explicitlyConfirmed } = appleFacts(props);
  // Two different facts, and the page owes each of them one thing. `confirmed`
  // is the S21 truth (the checklist went through, or the saved registration
  // already reads submitted/active): once it holds there is nothing left to
  // set up at Apple, so the guide and the checklist go away. `closed` is the
  // client's own confirmation, which carries the rail mark (F118) - until it
  // lands, the step keeps exactly one control: that confirmation.
  const closed = explicitlyConfirmed || confirmationState === "done";
  // F175 (David Riha 2026-10-20): "can the page first ask 'Do you already have
  // an Apple Developer account?' and adjust based on what they answer? The
  // current state is a bit confusing if they do already have it." The answer
  // is a view choice, not a stored fact: what IS stored is the checklist the
  // existing-account path submits (registration `active`).
  // Only an owner on an OPEN step is asked: a closed checklist must keep its
  // reason on screen (F144), and nobody else can answer for the account.
  const checklistLock = appleChecklistLock(props, section);
  const [answer, setHasAccount] = useState<"yes" | "no" | null>(props.appleHasAccount ?? null);
  const hasAccount = checklistLock.locked ? "no" : answer;
  const existingPath = !closed && !confirmed && hasAccount === "yes";

  async function confirmAppleAccount() {
    setConfirmationState("sending");
    try {
      const response = await fetch("/api/client-steps/apple/confirm", {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "X-Launchpad-CSRF": props.csrfToken ?? "",
          "Idempotency-Key": crypto.randomUUID()
        },
        body: "{}"
      });
      const body = await response.json() as { ok?: boolean };
      if (!response.ok || body.ok !== true) throw new Error("apple_confirmation_failed");
      setConfirmationState("done");
      await refreshClientFact(props);
    } catch {
      setConfirmationState("error");
    }
  }

  return (
    <section className="m5b-view app-step-view" data-app-step="apple" aria-labelledby="app-apple-title">
      <header className="m5b-heading">
        <p className="eyebrow blue-eyebrow">{props.t("app.apple.eyebrow")}</p>
        <h1 id="app-apple-title">{props.t(existingPath ? "app.apple.existing.heading" : "rail.steps.apple")}</h1>
        <p className="lead">{props.t(existingPath ? "app.apple.existing.lead" : "app.apple.lead")}</p>
      </header>
      {props.dataErrors?.has("resources") && <DataError t={props.t} />}

      {closed ? (
        <p className="section-action-note success-note" role="status">{props.t("app.accountConfirmed")}</p>
      ) : (
        <>
          {confirmed ? (
            <p className="section-action-note">{props.t("app.apple.confirmPrompt")}</p>
          ) : hasAccount === null ? (
            <HaveAccountQuestion t={props.t} onAnswer={setHasAccount} />
          ) : (
            <>
              {!checklistLock.locked && (
                <button className="text-button apple-have-account-change" type="button" onClick={() => setHasAccount(null)}>
                  {props.t("app.apple.haveAccount.change")}
                </button>
              )}
              <p className="app-google-play-note">{props.t("app.release.googlePlayPublisher")}</p>
              {hasAccount === "no" && <AppleEnrollmentGuide t={props.t} />}
              <AppleChecklist
                initial={account}
                existing={hasAccount === "yes"}
                lock={checklistLock}
                busy={props.busyAction !== null}
                submitting={props.busyAction === "apple-start"}
                t={props.t}
                onSubmit={(payload) => props.onAction("apple-start", payload)}
              />
            </>
          )}
          <div className="section-action apple-confirm-action">
            {/* F175: no second confirm button beside the question or the
                existing-account form; that path closes with its own save, then
                this button (the F118 confirmation) appears. */}
            {props.membershipRole === "owner" && (confirmed || hasAccount === "no") && (
              <button
                className="primary-button"
                type="button"
                disabled={confirmationState === "sending"}
                onClick={() => void confirmAppleAccount()}
              >
                {props.t(confirmationState === "sending" ? "app.confirmingAccount" : "app.confirmAccount")}
              </button>
            )}
            {confirmationState === "error" && (
              <p className="error-message" role="alert">{props.t("app.confirmAccountError")}</p>
            )}
          </div>
        </>
      )}
      <ActionError t={props.t} issue={props.actionError} />
    </section>
  );
}

/** F175: the first thing the page asks, before any guide or checklist. */
export function HaveAccountQuestion(props: { t: (key: string) => string; onAnswer: (answer: "yes" | "no") => void }) {
  return (
    <section className="apple-have-account" aria-labelledby="apple-have-account-title">
      <h2 id="apple-have-account-title">{props.t("app.apple.haveAccount.question")}</h2>
      <p>{props.t("app.apple.haveAccount.copy")}</p>
      <div className="apple-have-account-actions">
        <button className="primary-button" type="button" onClick={() => props.onAnswer("yes")}>
          {props.t("app.apple.haveAccount.yes")}
        </button>
        <button className="secondary-button" type="button" onClick={() => props.onAnswer("no")}>
          {props.t("app.apple.haveAccount.no")}
        </button>
      </div>
    </section>
  );
}

export function AppleEnrollmentGuide({ t }: { t: (key: string) => string }) {
  const steps = [
    "choose",
    "secure",
    "legalDetails",
    "enroll",
    "company",
    "review",
    "finish"
  ] as const;
  return (
    <article className="apple-enrollment-guide" aria-labelledby="apple-enrollment-title">
      <header className="apple-guide-heading">
        <p className="eyebrow blue-eyebrow">{t("app.appleGuide.eyebrow")}</p>
        <h2 id="apple-enrollment-title">{t("app.appleGuide.title")}</h2>
        <p>{t("app.appleGuide.intro")}</p>
      </header>

      <section className="apple-guide-needs" aria-labelledby="apple-needs-title">
        <h3 id="apple-needs-title">{t("app.appleGuide.needsTitle")}</h3>
        <ul>
          {["appleId", "identity", "contact", "company"].map((key) => (
            <li key={key}>
              <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m5 12.5 4 4L19 7" /></svg>
              <span><NonBreakingDunsText text={t(`app.appleGuide.needs.${key}`)} /></span>
            </li>
          ))}
        </ul>
      </section>

      <section className="apple-guide-path-section" aria-labelledby="apple-path-title">
        <h3 id="apple-path-title">{t("app.appleGuide.pathTitle")}</h3>
        <div className="apple-guide-paths">
          {/* The product registers app buyers as an ORGANIZATION, so that is
              the path this page names first and marks as ours. */}
          <article className="apple-path-card recommended">
            <span>{t("app.appleGuide.path.recommended")}</span>
            <h4>{t("app.appleGuide.path.organizationTitle")}</h4>
            <p><NonBreakingDunsText text={t("app.appleGuide.path.organizationCopy")} /></p>
          </article>
          <article className="apple-path-card">
            <h4>{t("app.appleGuide.path.individualTitle")}</h4>
            <p><NonBreakingDunsText text={t("app.appleGuide.path.individualCopy")} /></p>
          </article>
        </div>
      </section>

      <DunsExplainer t={t} />

      <ol className="apple-guide-actions">
        {steps.map((key, index) => (
          <li key={key}>
            <span className="apple-guide-number step-number-token" aria-hidden="true">{index + 1}</span>
            <div>
              <h3>{t(`app.appleGuide.step.${key}Title`)}</h3>
              <p><NonBreakingDunsText text={t(`app.appleGuide.step.${key}Copy`)} /></p>
              {key === "enroll" && (
                <div className="apple-guide-links">
                  <a href="https://developer.apple.com/programs/enroll/" target="_blank" rel="noreferrer">
                    {t("app.appleGuide.webLink")}
                  </a>
                  <a href="https://apps.apple.com/app/apple-developer/id640199958" target="_blank" rel="noreferrer">
                    {t("app.appleGuide.appLink")}
                  </a>
                </div>
              )}
            </div>
          </li>
        ))}
      </ol>

      <aside className="apple-guide-blockers">
        <h3>{t("app.appleGuide.blockersTitle")}</h3>
        <ul>
          {["nickname", "address", "twoFactor", "duns", "website"].map((key) => (
            <li key={key}>{t(`app.appleGuide.blockers.${key}`)}</li>
          ))}
        </ul>
      </aside>
      <p className="apple-guide-approval-note">{t("app.appleGuide.approvalNote")}</p>
    </article>
  );
}

/**
 * F144: what D-U-N-S is, and how to get one (Pavel 2026-08-13: "Tam někde musí
 * být vysvětleno, co to je DUNS a jak to vytvořit. Co to je to D-U-N-S? Já to
 * třeba vůbec nevím. Takže je to pro mě dost chaotický.")
 *
 * Before this the word appeared FIVE times on the page - in the account type
 * note, the checkbox, the needs list, the organization card and the company
 * step - and not once did anything say what it is, that it costs nothing, or
 * where to get one. The client was asked to tick a box about a thing they had
 * never heard of.
 *
 * 🔴 This is an ADDITION, not a correction: the platform's own guidance
 * (buddy `features.mobileApp`) has no D-U-N-S section at all, so nothing here
 * may contradict what it does say - organization, 99 USD a year, Apple
 * verifies the company in 1 to 2 working days. The waiting time below belongs
 * to Dun & Bradstreet and lands BEFORE the enrolment, which is exactly why the
 * copy names it as the step that comes first.
 */
export function DunsExplainer({ t }: { t: (key: string) => string }) {
  return (
    // 🔴 Every string here goes through NonBreakingDunsText. At 390px the card
    // wraps hard and the code broke as "D-U-" / "N-S" across two lines, inside
    // the button label as well - the exact QC W1b rule this component exists
    // for. The structural tests all passed; the mobile screenshot is what
    // caught it.
    <section className="apple-duns-card" aria-labelledby="apple-duns-title">
      <h3 id="apple-duns-title"><NonBreakingDunsText text={t("app.duns.title")} /></h3>
      <p><NonBreakingDunsText text={t("app.duns.whatIs")} /></p>
      <p><NonBreakingDunsText text={t("app.duns.whyApple")} /></p>

      {/* The explanation stands BEFORE the control it explains: the reader has
          to know what the button does before pressing it. 🔴 The note may NOT
          wear `.apple-duns-note` - that class already means the amber railed
          requirement note inside the checklist, and borrowing it dragged an
          amber rail and its background onto a plain sentence. */}
      <div className="apple-duns-action">
        <p className="apple-duns-free"><NonBreakingDunsText text={t("app.duns.free")} /></p>
        <p className="apple-duns-lookup-note"><NonBreakingDunsText text={t("app.duns.lookupCopy")} /></p>
        {/* 🔴 The label is wrapped in ONE span. `a.secondary-button` is
            `display: inline-flex`, so the spans NonBreakingDunsText emits
            would each become a flex ITEM and flex trims the whitespace at
            their edges: the button rendered "OvěřitD-U-N-S u Applu". One
            wrapper keeps the anchor at a single flex item and the token spans
            inline inside it. */}
        <a className="secondary-button" href={DUNS_LOOKUP_URL} target="_blank" rel="noreferrer">
          <span><NonBreakingDunsText text={t("app.duns.lookupLink")} /></span>
        </a>
      </div>

      <div className="apple-duns-grid">
        <div>
          <h4>{t("app.duns.prepareTitle")}</h4>
          <ul>
            {["legalName", "address", "contact"].map((key) => (
              <li key={key}><NonBreakingDunsText text={t(`app.duns.prepare.${key}`)} /></li>
            ))}
          </ul>
        </div>
        <div>
          <h4>{t("app.duns.timingTitle")}</h4>
          <p><NonBreakingDunsText text={t("app.duns.timingCopy")} /></p>
          <h4>{t("app.duns.mismatchTitle")}</h4>
          <p><NonBreakingDunsText text={t("app.duns.mismatchCopy")} /></p>
        </div>
      </div>

      <p className="apple-duns-individual"><NonBreakingDunsText text={t("app.duns.individualNote")} /></p>
    </section>
  );
}

// The honest S21 confirmation: the client ticks only what really happened;
// submit fires the existing /api/app/apple-account-started chain.
//
// F135d: the Team ID and the App Store Connect e-mail are collected HERE, in
// the same payload, because they are the two facts the product asks for right
// after the enrolment confirmation - and the wizard step that used to be
// their only home does not open until the sales page is approved, twelve
// steps later. Both stay optional so a missing code can never block the
// confirmation itself.
export function AppleChecklist(props: {
  initial: Record<string, unknown> | null;
  /**
   * F175: the client already HAS an account. An active membership is the
   * fact Apple only grants after two-factor, the fee and (for a company) the
   * D-U-N-S check, so one honest tick stands for all four; what this path
   * really needs is the Team ID and the App Store Connect e-mail.
   */
  existing?: boolean;
  // F144: the closed state and the sentence that explains it, from ONE fact.
  lock: { locked: boolean; reasonKey: string | null };
  busy: boolean;
  submitting: boolean;
  t: (key: string) => string;
  onSubmit: (payload: Record<string, unknown>) => void | Promise<void>;
}) {
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(props.initial?.two_factor_enabled === true);
  const [accountType, setAccountType] = useState(String(props.initial?.account_type ?? "organization"));
  const [dunsRequired, setDunsRequired] = useState(
    props.initial?.duns_required === true || props.initial?.account_type !== "individual"
  );
  const [dunsConfirmed, setDunsConfirmed] = useState(props.initial?.duns_confirmed === true);
  const [feeConfirmed, setFeeConfirmed] = useState(props.initial?.fee_confirmed === true);
  const [registrationStatus, setRegistrationStatus] = useState(String(props.initial?.registration_status ?? "not_started"));
  const [teamId, setTeamId] = useState(String(props.initial?.team_id ?? ""));
  const [connectEmail, setConnectEmail] = useState(String(props.initial?.app_store_connect_email ?? ""));
  // F144: the submit used to sit grey and mute whenever the checklist was not
  // finished - the everyday state of this form, and the one Pavel met. It names
  // what it is still waiting for instead (the F44/F85 contract: name what
  // blocks the submit, never "fill in the form above").
  const [membershipActive, setMembershipActive] = useState(props.initial?.registration_status === "active");
  const existing = props.existing === true;
  const missing = (existing ? [
    // Team ID and e-mail stay optional here too (F135d: a missing code must
    // never block the confirmation); the copy asks for them.
    !membershipActive ? props.t("app.apple.existing.activeShort") : null
  ] : [
    !twoFactorEnabled ? props.t("app.check.twoFactor") : null,
    dunsRequired && !dunsConfirmed ? props.t("app.check.dunsConfirmed") : null,
    !feeConfirmed ? props.t("app.check.fee") : null,
    !["submitted", "active"].includes(registrationStatus) ? props.t("app.check.registrationMissing") : null
  ]).filter((item): item is string => item !== null);
  const complete = missing.length === 0;
  const disabled = props.lock.locked || props.busy;
  function submit(event: FormEvent) {
    event.preventDefault();
    if (!complete) return;
    void props.onSubmit({
      twoFactorEnabled: existing ? true : twoFactorEnabled,
      accountType,
      dunsRequired,
      dunsConfirmed: existing ? dunsRequired : dunsConfirmed,
      feeConfirmed: existing ? true : feeConfirmed,
      registrationStatus: existing ? "active" : registrationStatus,
      teamId: teamId.trim(),
      appStoreConnectEmail: connectEmail.trim()
    });
  }
  return (
    <form className="apple-checklist" onSubmit={submit}>
      <h2>{props.t(existing ? "app.apple.existing.title" : "app.checklistTitle")}</h2>
      <p>{props.t(existing ? "app.apple.existing.copy" : "app.checklistCopy")}</p>
      {/* 🔴 F144: a closed control says why, BESIDE itself. The whole form used
          to sit disabled with nothing on the page explaining it, which is what
          "nejde na to klikat" was: the client had no way to tell a refusal from
          a broken screen. */}
      {props.lock.reasonKey && (
        <p className="apple-checklist-closed" role="status">{props.t(props.lock.reasonKey)}</p>
      )}
      {existing && (
        <label className="check-row"><input type="checkbox" checked={membershipActive} disabled={disabled} onChange={(event) => setMembershipActive(event.target.checked)} /><span>{props.t("app.apple.existing.active")}</span></label>
      )}
      {!existing && <>
      <label className="check-row"><input type="checkbox" checked={twoFactorEnabled} disabled={disabled} onChange={(event) => setTwoFactorEnabled(event.target.checked)} /><span>{props.t("app.check.twoFactor")}</span></label>
      <p className="apple-check-note">{props.t("app.check.twoFactorHelp")}</p>
      </>}
      <label className="field-row"><span>{props.t("app.check.accountType")}</span><select value={accountType} disabled={disabled} onChange={(event) => {
        const next = event.target.value;
        setAccountType(next);
        setDunsRequired(next === "organization");
        if (next === "individual") setDunsConfirmed(false);
      }}><option value="organization">{props.t("app.account.organization")}</option><option value="individual">{props.t("app.account.individual")}</option></select></label>
      <p className="apple-check-note">{props.t("app.check.accountTypeHelp")}</p>
      {!existing && <>
      {dunsRequired && <p className="apple-duns-note"><NonBreakingDunsText text={props.t("app.check.dunsRequired")} /></p>}
      {dunsRequired && <label className="check-row"><input type="checkbox" checked={dunsConfirmed} disabled={disabled} onChange={(event) => setDunsConfirmed(event.target.checked)} /><span><NonBreakingDunsText text={props.t("app.check.dunsConfirmed")} /></span></label>}
      {dunsRequired && (
        <p className="apple-check-note">
          <NonBreakingDunsText text={props.t("app.check.dunsConfirmedHelp")} />{" "}
          <a href={DUNS_LOOKUP_URL} target="_blank" rel="noreferrer">{props.t("app.duns.lookupLink")}</a>
        </p>
      )}
      <label className="check-row"><input type="checkbox" checked={feeConfirmed} disabled={disabled} onChange={(event) => setFeeConfirmed(event.target.checked)} /><span>{props.t("app.check.fee")}</span></label>
      <p className="apple-check-note">{props.t("app.check.feeHelp")}</p>
      <label className="field-row"><span>{props.t("app.check.registration")}</span><select value={registrationStatus} disabled={disabled} onChange={(event) => setRegistrationStatus(event.target.value)}><option value="not_started">{props.t("app.registration.notStarted")}</option><option value="submitted">{props.t("app.registration.submitted")}</option><option value="active">{props.t("app.registration.active")}</option></select></label>
      <p className="apple-check-note">{props.t("app.check.registrationHelp")}</p>
      </>}
      <label className="field-row apple-field-block">
        <span>{props.t("app.check.teamId")}</span>
        <input type="text" value={teamId} disabled={disabled} maxLength={20} autoComplete="off" onChange={(event) => setTeamId(event.target.value)} />
      </label>
      <p className="apple-field-note">{props.t("app.check.teamIdHelp")}</p>
      <label className="field-row apple-field-block">
        <span>{props.t("app.check.connectEmail")}</span>
        <input type="email" value={connectEmail} disabled={disabled} maxLength={200} autoComplete="off" onChange={(event) => setConnectEmail(event.target.value)} />
      </label>
      <p className="apple-field-note">{props.t("app.check.connectEmailHelp")}</p>
      <button className="primary-button compact-button" type="submit" disabled={disabled || !complete}>{props.submitting ? props.t("app.submitting") : props.t(existing ? "app.apple.existing.submit" : "app.submit")}</button>
      {!props.lock.locked && !complete && (
        <p className="apple-submit-needs" id="apple-submit-needs">
          {props.t("app.check.submitNeeds")}
          <span>{missing.join(" · ")}</span>
        </p>
      )}
    </form>
  );
}
